import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner, Badge, Accordion } from 'react-bootstrap';
import AdminToolsLayout from './AdminToolsLayout';

/**
 * Prompts Management Component
 * View and edit AI prompts used in article processing
 */
const PromptsManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editedPrompts, setEditedPrompts] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await fetch('/api/admin/tools/prompts', { credentials: 'include' });
      const data = await response.json();
      setPrompts(data.prompts);
      setLoading(false);
    } catch (err) {
      setError('Failed to load prompts');
      setLoading(false);
    }
  };

  const handleContentChange = (promptId, content) => {
    setEditedPrompts(prev => ({
      ...prev,
      [promptId]: { ...prev[promptId], content }
    }));
  };

  const handleSettingChange = (promptId, field, value) => {
    setEditedPrompts(prev => ({
      ...prev,
      [promptId]: { ...prev[promptId], [field]: value }
    }));
  };

  const savePrompt = async (prompt) => {
    const edits = editedPrompts[prompt.id];
    if (!edits) return;

    setSaving(prev => ({ ...prev, [prompt.id]: true }));
    setError('');
    setSuccess('');

    try {
      let response = await fetch(`/api/admin/tools/prompts/${prompt.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(edits)
      });

      let data = await response.json();

      // The template-integrity guard blocks a drastic content-shrink (the "NULL clobber"
      // signature). If the admin confirms this large edit is intentional, re-save with force.
      if (data.error && data.code === 'DRASTIC_SHRINK' &&
          window.confirm(`${data.error}\n\nSave anyway?`)) {
        response = await fetch(`/api/admin/tools/prompts/${prompt.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...edits, force: true })
        });
        data = await response.json();
      }

      if (data.error) {
        setError(data.error);
      } else {
        // Update local state with saved values
        setPrompts(prev => prev.map(p =>
          p.id === prompt.id ? { ...p, ...edits } : p
        ));
        // Clear edited state for this prompt
        setEditedPrompts(prev => {
          const next = { ...prev };
          delete next[prompt.id];
          return next;
        });
        setSuccess(`"${prompt.prompt_name}" saved successfully`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to save prompt');
    }

    setSaving(prev => ({ ...prev, [prompt.id]: false }));
  };

  const hasChanges = (promptId) => {
    return !!editedPrompts[promptId];
  };

  const getPromptContent = (prompt) => {
    return editedPrompts[prompt.id]?.content ?? prompt.content;
  };

  const categoryLabels = {
    metadata_extraction: 'Metadata Extraction',
    social_posts: 'Social Media Posts',
    tldr: 'TLDR Summary',
    image: 'Image Generation',
    audio: 'Audio Generation',
    alt_headlines: 'Alternative Headlines',
    image_alt_text: 'Image Alt Text'
  };

  const categoryDescriptions = {
    metadata_extraction: 'Extracts title, author, date, and content from article HTML',
    social_posts: 'Generates 3 Facebook posts with different angles',
    tldr: 'Creates a 5-bullet summary of the article',
    image: 'Generates a social media image based on the article',
    audio: 'Audio generation prompts (summary and reading modes)',
    alt_headlines: 'Alternative headlines for Web, Facebook, TikTok, Instagram, YouTube (25 total per article)',
    image_alt_text: 'Alt text generation from generated images via vision model'
  };

  const typeColors = {
    system: 'primary',
    user: 'success',
    composite: 'info'
  };

  // Group prompts by category
  const groupedPrompts = prompts.reduce((acc, prompt) => {
    if (!acc[prompt.category]) {
      acc[prompt.category] = [];
    }
    acc[prompt.category].push(prompt);
    return acc;
  }, {});

  if (loading) {
    return (
      <AdminToolsLayout>
        <div className="text-center py-4"><Spinner animation="border" /></div>
      </AdminToolsLayout>
    );
  }

  return (
    <AdminToolsLayout>
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">AI Prompts</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <p className="text-muted mb-4">
            Edit the prompts used by the Article to Media tool. Variables are shown as <code>{'{{variable}}'}</code> and will be replaced with actual content during processing.
          </p>

          <div className="alert alert-info mb-4">
            <strong>System vs User Prompts:</strong> Text generation uses the OpenAI Chat API which takes two message types:
            <ul className="mb-0 mt-2">
              <li><Badge bg="primary">system</Badge> - Defines <em>how</em> the AI should behave (its role, output format, constraints)</li>
              <li><Badge bg="success">user</Badge> - Contains <em>what</em> to process (the actual data with variable placeholders)</li>
              <li><Badge bg="info">composite</Badge> - Single prompts for non-chat APIs (images, audio)</li>
            </ul>
          </div>

          <Accordion defaultActiveKey="0">
            {Object.entries(groupedPrompts).map(([category, categoryPrompts], categoryIndex) => (
              <Accordion.Item eventKey={String(categoryIndex)} key={category}>
                <Accordion.Header>
                  <div>
                    <strong>{categoryLabels[category] || category}</strong>
                    <small className="text-muted ms-2">({categoryPrompts.length} prompt{categoryPrompts.length !== 1 ? 's' : ''})</small>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted small mb-3">{categoryDescriptions[category]}</p>

                  {categoryPrompts.map(prompt => (
                    <Card key={prompt.id} className="mb-3">
                      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                        <div>
                          <Badge bg={typeColors[prompt.prompt_type]} className="me-2">
                            {prompt.prompt_type}
                          </Badge>
                          <span className="fw-bold">{prompt.prompt_name}</span>
                        </div>
                        <div>
                          {hasChanges(prompt.id) && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => savePrompt(prompt)}
                              disabled={saving[prompt.id]}
                            >
                              {saving[prompt.id] ? <Spinner animation="border" size="sm" /> : 'Save'}
                            </Button>
                          )}
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <Form.Group className="mb-3">
                          <Form.Control
                            as="textarea"
                            rows={prompt.content.split('\n').length + 2}
                            value={getPromptContent(prompt)}
                            onChange={(e) => handleContentChange(prompt.id, e.target.value)}
                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                          />
                        </Form.Group>

                        {prompt.variables && prompt.variables.length > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">Available variables: </small>
                            {prompt.variables.map((v, idx) => (
                              <Badge
                                key={idx}
                                bg="secondary"
                                className="me-1"
                                title={v.description}
                                style={{ cursor: 'help' }}
                              >
                                {`{{${v.name}}}`}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {(prompt.temperature !== null || prompt.max_tokens !== null) && (
                          <div className="d-flex gap-3 mt-2">
                            {prompt.temperature !== null && (
                              <Form.Group style={{ width: '120px' }}>
                                <Form.Label className="small mb-1">Temperature</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  step="0.1"
                                  min="0"
                                  max="2"
                                  value={editedPrompts[prompt.id]?.temperature ?? prompt.temperature}
                                  onChange={(e) => handleSettingChange(prompt.id, 'temperature', parseFloat(e.target.value))}
                                />
                              </Form.Group>
                            )}
                            {prompt.max_tokens !== null && (
                              <Form.Group style={{ width: '120px' }}>
                                <Form.Label className="small mb-1">Max Tokens</Form.Label>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  step="100"
                                  min="100"
                                  value={editedPrompts[prompt.id]?.max_tokens ?? prompt.max_tokens}
                                  onChange={(e) => handleSettingChange(prompt.id, 'max_tokens', parseInt(e.target.value))}
                                />
                              </Form.Group>
                            )}
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>
    </AdminToolsLayout>
  );
};

export default PromptsManagement;
